// @ts-nocheck
/**
 * Deck Info System - デッキ情報購入システム
 *
 * NPCのデッキ情報を購入・管理する
 *
 * Created: 2025-11-29
 */

import { TOURNAMENT_TYPES, TOURNAMENT_CONFIG } from './tournamentManager';
import { COMPETITORS, DECKS, getDeck } from '../data/competitors';

// ========================================
// 情報タイプ定義
// ========================================

export const INFO_TYPES = {
  DECK_TYPE: 'deckType',           // 戦術タイプ（コンセプト説明）
  MATCHUP_HISTORY: 'matchupHistory', // 対戦履歴（特定相手との戦績）
  FULL_ANALYSIS: 'fullAnalysis',     // 総合分析（全対戦履歴 + 得意/苦手）
};

export const INFO_TYPE_NAMES = {
  [INFO_TYPES.DECK_TYPE]: '戦術タイプ',
  [INFO_TYPES.MATCHUP_HISTORY]: '対戦履歴',
  [INFO_TYPES.FULL_ANALYSIS]: '総合分析',
};

// ========================================
// 価格設定
// ========================================

/**
 * 大会タイプ別の情報価格
 */
export const INFO_PRICES = {
  [TOURNAMENT_TYPES.DAILY]: {
    [INFO_TYPES.DECK_TYPE]: 500,
    [INFO_TYPES.MATCHUP_HISTORY]: 1500,
    [INFO_TYPES.FULL_ANALYSIS]: 3000,
  },
  [TOURNAMENT_TYPES.WEEKLY]: {
    [INFO_TYPES.DECK_TYPE]: 1000,
    [INFO_TYPES.MATCHUP_HISTORY]: 1500,
    [INFO_TYPES.FULL_ANALYSIS]: 3000,
  },
  [TOURNAMENT_TYPES.MAJOR]: {
    [INFO_TYPES.DECK_TYPE]: 2000,
    [INFO_TYPES.MATCHUP_HISTORY]: 1500,
    [INFO_TYPES.FULL_ANALYSIS]: 3000,
  },
};

// ========================================
// 情報取得
// ========================================

/**
 * 戦術タイプ（コンセプト説明）を取得
 * @param {string} deckKey - デッキキー（デッキ名）
 * @returns {Object} { name, type, concept, attribute }
 */
export function getDeckType(deckKey) {
  const deck = getDeck(deckKey);
  if (!deck) return { name: '不明', type: '不明', concept: '', attribute: '' };

  return {
    name: deck.name || deckKey,
    type: deck.type || '不明',
    concept: deck.concept || '',
    attribute: deck.attribute || '',
  };
}

/**
 * 特定の相手との対戦履歴を取得
 * @param {string} competitorId - 対象NPC ID
 * @param {string} opponentId - 相手NPC ID
 * @param {Array} history - 大会履歴配列
 * @returns {Object} { wins, losses, total, recentResults }
 */
export function getMatchupHistory(competitorId, opponentId, history = []) {
  let wins = 0;
  let losses = 0;
  const recentResults = []; // 直近5戦の結果（● = 勝ち、○ = 負け）

  for (const tournament of history) {
    if (!tournament.results) continue;

    // results は rounds 配列：[{ roundNum, matches: [...] }, ...]
    for (const round of tournament.results) {
      if (!round.matches) continue;

      for (const match of round.matches) {
        // competitorId vs opponentId の試合を探す
        const isMatch =
          (match.p1 === competitorId && match.p2 === opponentId) ||
          (match.p1 === opponentId && match.p2 === competitorId);

        if (!isMatch) continue;

        const didWin = match.winner === competitorId;
        if (didWin) {
          wins++;
        } else {
          losses++;
        }

        // 直近5戦分を記録
        if (recentResults.length < 5) {
          recentResults.push(didWin ? '●' : '○');
        }
      }
    }
  }

  return {
    wins,
    losses,
    total: wins + losses,
    recentResults,
  };
}

/**
 * 全対戦履歴と総合分析を取得
 * @param {string} competitorId - 対象NPC ID
 * @param {Array} participants - 大会参加者リスト
 * @param {Array} history - 大会履歴配列
 * @returns {Object} { matchups, strongAgainst, weakAgainst, totalWins, totalLosses }
 */
export function getFullAnalysis(competitorId, participants, history = []) {
  const matchups = {};
  let totalWins = 0;
  let totalLosses = 0;

  // 各参加者との対戦履歴を集計
  for (const opponentId of participants) {
    if (opponentId === competitorId) continue;

    const record = getMatchupHistory(competitorId, opponentId, history);
    matchups[opponentId] = record;
    totalWins += record.wins;
    totalLosses += record.losses;
  }

  // 得意/苦手相手を判定（勝率ベース、最低2戦以上）
  const strongAgainst = [];
  const weakAgainst = [];

  for (const [opponentId, record] of Object.entries(matchups)) {
    if (record.total < 2) continue; // データ不足

    const winRate = record.wins / record.total;
    if (winRate >= 0.6) {
      strongAgainst.push({ id: opponentId, winRate, record });
    } else if (winRate <= 0.4) {
      weakAgainst.push({ id: opponentId, winRate, record });
    }
  }

  // 勝率順でソート
  strongAgainst.sort((a, b) => b.winRate - a.winRate);
  weakAgainst.sort((a, b) => a.winRate - b.winRate);

  return {
    matchups,
    strongAgainst,
    weakAgainst,
    totalWins,
    totalLosses,
    totalMatches: totalWins + totalLosses,
  };
}

// ========================================
// 購入管理
// ========================================

/**
 * 情報購入の価格を取得
 * @param {string} tournamentType - 大会タイプ
 * @param {string} infoType - 情報タイプ
 * @returns {number} 価格
 */
export function getInfoPrice(tournamentType, infoType) {
  const prices = INFO_PRICES[tournamentType];
  if (!prices) return 0;
  return prices[infoType] || 0;
}

/**
 * 情報購入をバリデーション
 * @param {Object} params - パラメータ
 * @param {string} params.competitorId - NPC ID
 * @param {string} params.infoType - 情報タイプ
 * @param {Object} params.tournament - 大会データ
 * @param {Object} params.purchasedInfo - 購入済み情報
 * @param {number} params.playerGold - 所持金
 * @returns {Object} { valid: boolean, error?: string }
 */
export function validateInfoPurchase({ competitorId, infoType, tournament, purchasedInfo, playerGold }) {
  // 大会チェック
  if (!tournament) {
    return { valid: false, error: '開催中の大会がありません' };
  }

  // 参加者チェック
  if (!tournament.participants.includes(competitorId)) {
    return { valid: false, error: 'この選手は参加していません' };
  }

  // 情報タイプチェック
  if (!Object.values(INFO_TYPES).includes(infoType)) {
    return { valid: false, error: '不明な情報タイプです' };
  }

  // 既に購入済みかチェック
  const key = `${tournament.id}_${competitorId}`;
  const existingInfo = purchasedInfo?.[key];
  if (existingInfo) {
    if (infoType === INFO_TYPES.DECK_TYPE && existingInfo.deckType) {
      return { valid: false, error: '戦術タイプは購入済みです' };
    }
    if (infoType === INFO_TYPES.MATCHUP_HISTORY && existingInfo.matchupHistory) {
      return { valid: false, error: '対戦履歴は購入済みです' };
    }
    if (infoType === INFO_TYPES.FULL_ANALYSIS && existingInfo.fullAnalysis) {
      return { valid: false, error: '総合分析は購入済みです' };
    }
  }

  // 価格チェック
  const price = getInfoPrice(tournament.type, infoType);
  if (price > playerGold) {
    return { valid: false, error: `所持金が不足しています（${price.toLocaleString()}G必要）` };
  }

  return { valid: true, price };
}

/**
 * 情報を購入
 * @param {Object} tournamentData - 大会データ
 * @param {Object} params - 購入パラメータ
 * @returns {Object} { success, tournamentData, info, price }
 */
export function purchaseInfo(tournamentData, { competitorId, infoType, tournament }) {
  const purchasedInfo = tournamentData.purchasedInfo || {};
  const history = tournamentData.history || [];
  const key = `${tournament.id}_${competitorId}`;
  const existingInfo = purchasedInfo[key] || {};

  // 使用中のデッキを取得
  const participantInfo = tournament.participantDecks?.[competitorId];
  const deckKey = participantInfo?.deckKey;

  // 情報を取得
  let info = {};
  switch (infoType) {
    case INFO_TYPES.DECK_TYPE:
      if (!deckKey) {
        return { success: false, error: 'デッキ情報が見つかりません' };
      }
      info = { deckType: getDeckType(deckKey) };
      break;
    case INFO_TYPES.MATCHUP_HISTORY:
      // 大会参加者全員との対戦履歴を取得
      const matchupData = {};
      for (const opponentId of tournament.participants) {
        if (opponentId === competitorId) continue;
        matchupData[opponentId] = getMatchupHistory(competitorId, opponentId, history);
      }
      info = { matchupHistory: matchupData };
      break;
    case INFO_TYPES.FULL_ANALYSIS:
      info = {
        fullAnalysis: getFullAnalysis(competitorId, tournament.participants, history),
      };
      break;
    default:
      return { success: false, error: '不明な情報タイプ' };
  }

  const price = getInfoPrice(tournament.type, infoType);

  // 購入情報を更新
  const newPurchasedInfo = {
    ...purchasedInfo,
    [key]: {
      ...existingInfo,
      ...info,
      competitorId,
      tournamentId: tournament.id,
    },
  };

  return {
    success: true,
    tournamentData: {
      ...tournamentData,
      purchasedInfo: newPurchasedInfo,
    },
    info,
    price,
  };
}

/**
 * 購入済み情報を取得
 * @param {Object} tournamentData - 大会データ
 * @param {string} tournamentId - 大会ID
 * @param {string} competitorId - NPC ID
 * @returns {Object|null} 購入済み情報
 */
export function getPurchasedInfo(tournamentData, tournamentId, competitorId) {
  const key = `${tournamentId}_${competitorId}`;
  return tournamentData.purchasedInfo?.[key] || null;
}

/**
 * 特定の情報タイプが購入済みかチェック
 * @param {Object} tournamentData - 大会データ
 * @param {string} tournamentId - 大会ID
 * @param {string} competitorId - NPC ID
 * @param {string} infoType - 情報タイプ
 * @returns {boolean}
 */
export function hasInfo(tournamentData, tournamentId, competitorId, infoType) {
  const info = getPurchasedInfo(tournamentData, tournamentId, competitorId);
  if (!info) return false;

  switch (infoType) {
    case INFO_TYPES.DECK_TYPE:
      return !!info.deckType;
    case INFO_TYPES.MATCHUP_HISTORY:
      return !!info.matchupHistory;
    case INFO_TYPES.FULL_ANALYSIS:
      return !!info.fullAnalysis;
    default:
      return false;
  }
}

// ========================================
// ヘルパー関数
// ========================================

/**
 * NPCの表示名を取得
 * @param {string} competitorId - NPC ID
 * @returns {string}
 */
export function getCompetitorName(competitorId) {
  const competitor = COMPETITORS[competitorId];
  return competitor?.name || competitorId;
}

/**
 * NPCの分析コメントを生成
 * @param {string} competitorId - NPC ID
 * @param {string} deckKey - デッキキー
 * @returns {string}
 */
export function getAnalysisComment(competitorId, deckKey) {
  const competitor = COMPETITORS[competitorId];
  const deck = getDeck(deckKey);

  if (!competitor || !deck) return '';

  const style = competitor.style || '';
  const deckType = deck.type || '';
  const deckAttribute = deck.attribute || competitor.attribute || '';

  // 簡易的な分析コメント
  const comments = [];

  if (style === '速攻' || deckType.includes('アグロ')) {
    comments.push('序盤の攻撃力に注意');
  } else if (style === 'コントロール' || deckType.includes('コントロール')) {
    comments.push('長期戦に備えた構成');
  } else if (style === 'バーン' || deckType.includes('バーン')) {
    comments.push('削りダメージに警戒');
  } else if (style === 'ビート' || deckType.includes('ビート')) {
    comments.push('高攻撃力のモンスターが中心');
  }

  if (deckAttribute) {
    comments.push(`${deckAttribute}属性が主力`);
  }

  if (deck.concept) {
    // コンセプトから短い説明を抽出
    const shortConcept = deck.concept.split('。')[0];
    if (shortConcept.length < 30) {
      comments.push(shortConcept);
    }
  }

  return comments.join('。');
}
